import React, { useMemo } from "react";
import SVGIcon, { SVGIconName } from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";
import { Sticker } from "web/react/emo/sticker";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { gettext } from "web/script/modules/django-i18n";
import { formatDateMY } from "web/script/utils/dates";
import * as styles from "./storefront-info.css";

interface MoreInfoItemProps {
    iconName: SVGIconName;
    text: string;
    key: string;
}

function MoreInfoItem({ iconName, text, key }: MoreInfoItemProps): React.ReactElement {
    return (
        <HStack spacing="xxs" align="center" key={key}>
            <SVGIcon className={styles.moreInfoIcon} name={iconName} />
            <Text textStyle="body-s">{text}</Text>
        </HStack>
    );
}

interface StorefrontInfoProps {
    description?: string;
    dateAdded: string | null;
    shipsToCountry: string | null;
    bestShippingOption: string | null;
    isCheckout?: boolean;
    isOfficialStore?: boolean;
}

export function StorefrontInfo({
    description,
    dateAdded,
    shipsToCountry,
    bestShippingOption,
    isCheckout = false,
    isOfficialStore = false,
}: StorefrontInfoProps): React.ReactElement {
    const moreInfoItems = useMemo(() => {
        const items: MoreInfoItemProps[] = [];

        if (shipsToCountry) {
            items.push({
                iconName: "world",
                text: gettext("storefront.shipping_info.country", { country: shipsToCountry }),
                key: "ships-to",
            });
        }

        if (bestShippingOption) {
            items.push({
                iconName: "local-shipping-sharp",
                text: gettext("storefront.shipping_info.days", { days: bestShippingOption }),
                key: "shipping",
            });
        }

        if (dateAdded) {
            items.push({
                iconName: "time-sharp",
                text: gettext("storefront.joined_date", { date: formatDateMY(dateAdded) }),
                key: "date-added",
            });
        }

        return items;
    }, [dateAdded, bestShippingOption, shipsToCountry]);

    return (
        <VStack spacing="md">
            <VStack spacing="sm">
                {description && (
                    <Text dataTestId="storefront-info-description" textStyle="body-s">
                        <div dangerouslySetInnerHTML={{ __html: description }} />
                    </Text>
                )}
                {(isCheckout || isOfficialStore) && (
                    <HStack spacing="xxs">
                        {isCheckout && <Sticker name="lystShopPartner" />}
                        {isOfficialStore && <Sticker name="officialStore" />}
                    </HStack>
                )}
            </VStack>
            <VStack spacing="sm">
                {moreInfoItems.length > 0 && (
                    <VStack spacing="xs">
                        <Text textStyle="body-bold">{gettext("general.more_information")}</Text>
                        <VStack spacing="xxs">
                            {moreInfoItems.map(({ key, iconName, text }) => (
                                <MoreInfoItem key={key} iconName={iconName} text={text} />
                            ))}
                        </VStack>
                    </VStack>
                )}
                {isCheckout && (
                    <Text textStyle="body-xs" color="secondary">
                        {gettext("storefront.verified.disclaimer")}
                    </Text>
                )}
            </VStack>
        </VStack>
    );
}
